import { Users01 } from "@untitledui/icons";
import { useLoaderData, useLocation } from "react-router";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { Table, TableCard } from "@/components/application/table/table";
import { Pager, SearchBox } from "@/components/admin/list-tools";
import { Deferred, Loading, Skeleton, TableSkeleton } from "@/components/admin/loading";
import { Badge, BadgeWithDot } from "@/components/base/badges/badges";
import { formatDate, isAdmin, type Page, type PlatformUser } from "../lib/api.js";
import { PageHeader } from "./shell.jsx";

export function UserBadges({ user }: { user: PlatformUser }) {
  return (
    <span className="flex flex-wrap gap-1.5">
      {isAdmin(user) && (
        <Badge color="brand" size="sm">
          管理员
        </Badge>
      )}
      {user.banned ? (
        <BadgeWithDot color="error" size="sm">
          已封禁
        </BadgeWithDot>
      ) : (
        <BadgeWithDot color="success" size="sm">
          正常
        </BadgeWithDot>
      )}
    </span>
  );
}

const COLUMNS = ["姓名", "电子邮件", "状态", "注册时间"];

/** The table with its real headings, rows of placeholders, and where the count will be. */
export function ListSkeleton({ columns }: { columns: string[] }) {
  return (
    <Loading>
      <TableCard.Root>
        <TableSkeleton columns={columns} rows={8} />
      </TableCard.Root>
      <Skeleton className="h-5 w-20" />
    </Loading>
  );
}

export function UsersSkeleton() {
  return (
    <>
      <PageHeader title="用户" />
      <SearchBox label="按电子邮件搜索用户" />
      <ListSkeleton columns={COLUMNS} />
    </>
  );
}

export function UsersRoute() {
  const { data } = useLoaderData<{ data: Promise<Page<PlatformUser>> }>();
  // Keyed by the query, so a search or another page shows as loading instead of holding the old rows.
  const { search } = useLocation();
  return (
    <>
      <PageHeader title="用户" />
      <SearchBox label="按电子邮件搜索用户" />
      <Deferred key={search} data={data} fallback={<ListSkeleton columns={COLUMNS} />}>
        {(page) => <Users data={page} />}
      </Deferred>
    </>
  );
}

function Users({ data }: { data: Page<PlatformUser> }) {
  return (
    <>
      {data.items.length === 0 ? (
        <div className="rounded-xl bg-primary py-16 shadow-xs ring-1 ring-secondary">
          <EmptyState size="md">
            <EmptyState.Header pattern="none">
              <EmptyState.FeaturedIcon icon={Users01} color="gray" theme="modern" />
            </EmptyState.Header>
            <EmptyState.Content>
              <EmptyState.Title>没有匹配的用户</EmptyState.Title>
            </EmptyState.Content>
          </EmptyState>
        </div>
      ) : (
        <TableCard.Root>
          <Table aria-label="用户">
            <Table.Header>
              <Table.Head id="name" label="姓名" isRowHeader />
              <Table.Head id="email" label="电子邮件" />
              <Table.Head id="state" label="状态" />
              <Table.Head id="createdAt" label="注册时间" />
            </Table.Header>
            <Table.Body items={data.items}>
              {(u) => (
                <Table.Row id={u.id} href={`/users/${u.id}`}>
                  <Table.Cell className="font-medium text-primary">{u.name}</Table.Cell>
                  <Table.Cell>{u.email}</Table.Cell>
                  <Table.Cell>
                    <UserBadges user={u} />
                  </Table.Cell>
                  <Table.Cell>{formatDate(u.createdAt)}</Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </TableCard.Root>
      )}
      <Pager total={data.total} offset={data.offset} limit={data.limit} />
    </>
  );
}
